import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Folder, FileText, FileDown, Video, File, Puzzle, ScrollText, Lock, ArrowRight } from 'lucide-react';
import { getUsernameFromProfileId, getCoachByPlayerProfile, getPlayerProfile } from '../../../../services/firestoreService';
import { getLibraryItemsByIds } from '../../../../services/libraryService';
import { useAuth } from '../../../../contexts/AuthContext';
import { resolveUserTier } from '../../../../services/accessResolver';
import { canAccessContent, normalizeAccessLevel, ACCESS_LEVEL_CONFIG, USER_TIERS } from '../../../../constants/accessLevels';
import useBlockStyles from '../common/useBlockStyles';

const TYPE_ICONS = {
  folder: Folder,
  pgn: FileText,
  pdf: FileDown,
  video: Video,
  file: File,
  puzzle: Puzzle,
  instructions: ScrollText,
};

const STYLE_OPTIONS = {
  defaultBackground: 'section',
  defaultHeadingSize: 'medium',
  defaultAlignment: 'center',
  defaultButtonStyle: 'primary',
  defaultPadding: 'normal',
};

const LibraryShowcaseRenderer = ({ block, profileId, theme = {} }) => {
  const { content } = block;
  const [items, setItems] = useState([]);
  const [username, setUsername] = useState(null);
  const [displayName, setDisplayName] = useState(null);
  const [userTier, setUserTier] = useState(USER_TIERS.VISITOR);
  const [isLoading, setIsLoading] = useState(true);
  const { currentUser } = useAuth();
  const styles = useBlockStyles(content, theme, STYLE_OPTIONS);

  const title = content.title || 'Study Library';
  const description = content.description || '';
  const buttonText = content.buttonText || 'Browse Full Library';
  const selectedItemIds = content.selectedItems || [];
  const accent = styles.accentColor || '#749555';

  useEffect(() => {
    if (!profileId || selectedItemIds.length === 0) {
      setIsLoading(false);
      return;
    }

    const load = async () => {
      try {
        const [fetchedUsername, coach, profile] = await Promise.all([
          getUsernameFromProfileId(profileId),
          getCoachByPlayerProfile(profileId),
          getPlayerProfile(profileId),
        ]);
        setUsername(fetchedUsername);
        setDisplayName(profile?.firstName || profile?.displayName || fetchedUsername || 'Coach');

        if (coach?.id) {
          const [fetchedItems, tier] = await Promise.all([
            getLibraryItemsByIds(coach.id, selectedItemIds),
            resolveUserTier(currentUser?.uid || null, coach.id, coach.userId),
          ]);

          // Preserve the order from selectedItemIds
          const ordered = selectedItemIds
            .map((id) => fetchedItems.find((item) => item.id === id))
            .filter(Boolean);

          setItems(ordered);
          setUserTier(tier);
        }
      } catch (err) {
        console.error('Error loading library showcase:', err);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId, selectedItemIds.join(','), currentUser?.uid]);

  const libraryUrl = username ? `/library/${username}` : '#';
  const finderTitle = displayName ? `${displayName}'s Chess Library` : 'Chess Library';

  if (isLoading) {
    return (
      <section
        className="lsb-section"
        style={{ background: styles.sectionBackground }}
      >
        <div className="lsb-loading" style={{ color: styles.mutedColor }}>
          Loading...
        </div>
      </section>
    );
  }

  if (selectedItemIds.length === 0) {
    return (
      <section
        className="lsb-section"
        style={{ background: styles.sectionBackground }}
      >
        <div className="lsb-container">
          <div className="lsb-empty" style={{ color: styles.mutedColor }}>
            No library items selected. Add items in the block editor.
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="lsb-section" style={{ background: styles.sectionBackground }}>
      <div className={`lsb-container ${styles.sectionPaddingClass}`}>
        {/* Section heading */}
        <h2
          className={`lsb-heading ${styles.headingSizeClass} ${styles.headingAlignmentClass}`}
          style={{
            color: styles.headingColor,
            fontFamily: styles.headingFontStyle,
          }}
        >
          {title}
        </h2>
        {description && (
          <p
            className={`lsb-desc ${styles.headingAlignmentClass}`}
            style={{
              color: styles.bodyColor,
              fontFamily: styles.bodyFontStyle,
              opacity: 0.8,
            }}
          >
            {description}
          </p>
        )}

        {/* MacBook Finder Window */}
        <div
          className="lsb-window"
          style={{
            background: styles.cardBackground,
            borderColor: styles.borderColor,
          }}
        >
          {/* Title bar */}
          <div className="lsb-titlebar" style={{ borderColor: styles.borderColor }}>
            <div className="lsb-dots">
              <span className="lsb-dot lsb-dot-red" />
              <span className="lsb-dot lsb-dot-yellow" />
              <span className="lsb-dot lsb-dot-green" />
            </div>
            <span
              className="lsb-titlebar-text"
              style={{ color: styles.mutedColor, fontFamily: styles.bodyFontStyle }}
            >
              {finderTitle}
            </span>
            <div className="lsb-dots-spacer" />
          </div>

          {/* Icon grid */}
          <div className="lsb-grid">
            {items.map((item) => {
              const Icon = TYPE_ICONS[item.type] || File;
              const accessLevel = normalizeAccessLevel(item.accessLevel);
              const hasAccess = canAccessContent(userTier, accessLevel);
              const accessConfig = ACCESS_LEVEL_CONFIG[accessLevel];
              const isFolder = item.type === 'folder';
              const iconProps = isFolder ? { fill: 'currentColor' } : {};

              // Build navigation target
              let itemUrl;
              if (isFolder) {
                itemUrl = username ? `/library/${username}/${item.id}` : '#';
              } else if (item.type === 'puzzle') {
                itemUrl = username ? `/library/${username}/puzzle/${item.id}` : '#';
              } else {
                itemUrl = username ? `/library/${username}/view/${item.id}` : '#';
              }

              const upsellUrl = username ? `/me/${username}` : '#';

              return (
                <Link
                  key={item.id}
                  to={hasAccess ? itemUrl : upsellUrl}
                  className={`lsb-item ${!hasAccess ? 'lsb-item-locked' : ''}`}
                >
                  {/* Icon — unified accent color */}
                  <div
                    className="lsb-item-icon"
                    style={{ background: `${accent}18`, color: accent }}
                  >
                    <Icon size={32} {...iconProps} />
                    {!hasAccess && (
                      <div className="lsb-lock-overlay">
                        <Lock size={18} />
                      </div>
                    )}
                  </div>

                  {/* Title */}
                  <span
                    className="lsb-item-title"
                    style={{ color: styles.headingColor, fontFamily: styles.bodyFontStyle }}
                  >
                    {item.title}
                  </span>

                  {/* Access badge */}
                  <span
                    className="lsb-item-badge"
                    style={{ color: accessConfig?.color }}
                  >
                    {accessConfig?.label}
                  </span>
                </Link>
              );
            })}
          </div>
        </div>

        {/* CTA — text-only link */}
        <div className={`lsb-cta ${styles.headingAlignmentClass}`}>
          <Link
            to={libraryUrl}
            className="lsb-button"
            style={{ color: accent }}
          >
            {buttonText} <ArrowRight size={16} />
          </Link>
        </div>
      </div>

      <style>{`
        .lsb-section {
          width: 100%;
        }

        .lsb-container {
          max-width: 700px;
          margin: 0 auto;
        }

        .lsb-heading {
          margin: 0 0 28px;
          font-weight: 700;
        }

        .lsb-desc {
          margin: 0 0 24px;
          font-size: 1rem;
        }

        /* ── Finder Window ───────────────────────────────── */
        .lsb-window {
          border: 1px solid;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
        }

        .lsb-titlebar {
          display: flex;
          align-items: center;
          padding: 12px 16px;
          border-bottom: 1px solid;
          gap: 12px;
        }

        .lsb-dots {
          display: flex;
          gap: 6px;
          flex-shrink: 0;
        }

        .lsb-dots-spacer {
          width: 54px;
          flex-shrink: 0;
        }

        .lsb-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
        }

        .lsb-dot-red { background: #ff5f57; }
        .lsb-dot-yellow { background: #febc2e; }
        .lsb-dot-green { background: #28c840; }

        .lsb-titlebar-text {
          flex: 1;
          text-align: center;
          font-size: 0.8rem;
          font-weight: 500;
          letter-spacing: 0.3px;
        }

        /* ── Icon Grid ───────────────────────────────────── */
        .lsb-grid {
          display: flex;
          justify-content: center;
          gap: 24px;
          padding: 28px 24px;
          flex-wrap: wrap;
        }

        .lsb-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          padding: 16px 12px;
          border-radius: 10px;
          text-decoration: none;
          transition: background 0.2s;
          cursor: pointer;
          width: 120px;
          flex-shrink: 0;
        }

        .lsb-item:hover {
          background: rgba(255, 255, 255, 0.06);
        }

        .lsb-item-locked {
          opacity: 0.6;
        }

        .lsb-item-locked:hover {
          opacity: 0.85;
        }

        /* ── Item Icon ───────────────────────────────────── */
        .lsb-item-icon {
          position: relative;
          width: 64px;
          height: 64px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: box-shadow 0.2s;
        }

        .lsb-item:hover .lsb-item-icon {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .lsb-lock-overlay {
          position: absolute;
          inset: 0;
          border-radius: 14px;
          background: rgba(0, 0, 0, 0.45);
          backdrop-filter: blur(2px);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .lsb-item-title {
          font-size: 0.78rem;
          font-weight: 600;
          text-align: center;
          max-width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          line-height: 1.2;
        }

        .lsb-item-badge {
          font-size: 0.62rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        /* ── CTA — text link ─────────────────────────────── */
        .lsb-cta {
          margin-top: 24px;
        }

        .lsb-button {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 0;
          font-weight: 600;
          font-size: 0.95rem;
          text-decoration: none;
          background: none;
          border: none;
          transition: opacity 0.2s;
        }

        .lsb-button:hover {
          opacity: 0.8;
        }

        .lsb-button:hover svg {
          transform: translateX(3px);
        }

        .lsb-button svg {
          transition: transform 0.2s;
        }

        .lsb-loading,
        .lsb-empty {
          text-align: center;
          padding: 32px 0;
          opacity: 0.6;
        }

        /* ── Responsive ──────────────────────────────────── */
        @media (max-width: 640px) {
          .lsb-grid {
            gap: 16px;
            padding: 20px 16px;
          }

          .lsb-item {
            width: 100px;
          }

          .lsb-item-icon {
            width: 56px;
            height: 56px;
          }

          .lsb-item-icon svg {
            width: 26px;
            height: 26px;
          }
        }
      `}</style>
    </section>
  );
};

export default LibraryShowcaseRenderer;
